import React, { FC, useCallback, useRef } from "react";
import { AlternativeProductGroup } from "../../../hooks/useAlternativeProducts";
import { useHasBottomRootMenu } from "../../../hooks/useHasBottomRootMenu";
import { useOptionsMenu } from "../../../hooks/use-options-menu";
import { BottomMenuButton } from "../../bottom-menu/bottom-menu-button";
import { BottomMenuButtons, IBottomMenuButtonsRef } from "../../bottom-menu/buttons";
import { SvgIcon } from "../../svg-icon";
import "shared/styles/product-options.css";
import { useLabels } from "../../../hooks/useLabels";
import { labels } from "./labels";
import { MenuIds, useMenus } from "../../../hooks/useMenus";

interface IStartDateOptionButton {
	onStartDateOptionClick: () => void;
	showIconButtons: boolean;
	isActive: boolean;
	supportedStartDate: "month" | "year";
}

const StartDateOptionButton: FC<IStartDateOptionButton> = ({
	onStartDateOptionClick,
	showIconButtons,
	isActive,
	supportedStartDate,
}) => {
	const [[startDateText]] = useLabels([
		supportedStartDate === "year" ? labels.startYear : labels.startMonth,
	]);

	return (
		<BottomMenuButton
			onClick={onStartDateOptionClick}
			title={startDateText}
			isActive={isActive}
			iconName={showIconButtons ? "calendar" : undefined}
		/>
	);
};

interface IAlternativeProductsButtons {
	alternativeProductsGroups: AlternativeProductGroup[];
	showIconButtons: boolean;
	selectedAlternativeProductGroup: AlternativeProductGroup | undefined;
	isAlternativeProductsMenuOpen: boolean;
	onAlternativeProductClick: (group: AlternativeProductGroup, index: number) => void;
}

const AlternativeProductsButtons: FC<IAlternativeProductsButtons> = ({
	alternativeProductsGroups,
	showIconButtons,
	selectedAlternativeProductGroup,
	isAlternativeProductsMenuOpen,
	onAlternativeProductClick,
}) => {
	return (
		<>
			{alternativeProductsGroups?.map((group, index) => (
				<BottomMenuButton
					onClick={() => onAlternativeProductClick(group, index)}
					key={group.id}
					title={group.name}
					// can't use id of group since it is generated on each api call
					isActive={
						selectedAlternativeProductGroup?.name === group.name &&
						isAlternativeProductsMenuOpen
					}
				>
					{showIconButtons ? <SvgIcon svgUrl={group.icon} /> : undefined}
				</BottomMenuButton>
			))}
		</>
	);
};

interface IProductOptionGroupButtons {
	productOptionGroup: eXpress.core.IProductOptionGroup;
	showIconButtons: boolean;
	isActive: boolean;
	onProductOptionsGroupClick: () => void;
}

const ProductOptionsGroupButtons: FC<IProductOptionGroupButtons> = ({
	productOptionGroup,
	showIconButtons,
	isActive,
	onProductOptionsGroupClick,
}) => {
	return (
		<BottomMenuButton
			onClick={onProductOptionsGroupClick}
			key={productOptionGroup.name}
			title={productOptionGroup.name}
			isActive={isActive}
		>
			{showIconButtons && productOptionGroup.icon ? (
				<SvgIcon svgUrl={productOptionGroup.icon} />
			) : undefined}
		</BottomMenuButton>
	);
};

export const OptionsBottomMenu = () => {
	const {
		menuId,
		alternativeProductsGroups,
		productOptionGroup,
		supportedStartDate,
		showIconButtons,
		selectedAlternativeProductGroup,
		handleStartDateClick,
		handleAlternativeProductClick,
		handleProductOptionsClick,
	} = useOptionsMenu();

	const {
		actions: { setMenu },
	} = useMenus();
	const hasRootMenu = useHasBottomRootMenu();
	const bottomMenuRef = useRef<IBottomMenuButtonsRef>(null);

	const scrollIntoView = useCallback(
		(index: number) => {
			// + 1 to take back button into account
			bottomMenuRef.current?.scrollIntoView(index + (hasRootMenu ? 1 : 0));
		},
		[hasRootMenu],
	);

	return (
		<BottomMenuButtons
			ref={bottomMenuRef}
			showBackButton={hasRootMenu}
			onBack={() => setMenu(MenuIds.Root)}
		>
			{supportedStartDate && (
				<StartDateOptionButton
					showIconButtons={showIconButtons}
					isActive={menuId === MenuIds.DateSelection}
					supportedStartDate={supportedStartDate}
					onStartDateOptionClick={() => {
						handleStartDateClick();
						scrollIntoView(0);
					}}
				/>
			)}
			{!!alternativeProductsGroups?.length && (
				<AlternativeProductsButtons
					alternativeProductsGroups={alternativeProductsGroups}
					showIconButtons={showIconButtons}
					selectedAlternativeProductGroup={selectedAlternativeProductGroup}
					isAlternativeProductsMenuOpen={menuId === MenuIds.AlternativeProducts}
					onAlternativeProductClick={(group, index) => {
						handleAlternativeProductClick(group);
						scrollIntoView(index + (supportedStartDate ? 1 : 0));
					}}
				/>
			)}
			{productOptionGroup && (
				<ProductOptionsGroupButtons
					productOptionGroup={productOptionGroup}
					showIconButtons={showIconButtons}
					isActive={menuId === MenuIds.ProductOptions}
					onProductOptionsGroupClick={() => {
						handleProductOptionsClick();
						scrollIntoView(
							(alternativeProductsGroups?.length ?? 0) + (supportedStartDate ? 1 : 0),
						);
					}}
				/>
			)}
		</BottomMenuButtons>
	);
};
